import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChartBarSquareIcon,
  MapPinIcon,
  ScaleIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { craApi, teamApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

export default function CraDashboardPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const navigate = useNavigate();
  const { hasFeature, isLoading: featuresLoading } = useFeatureAccess();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [showCradModal, setShowCradModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['cra-dashboard', teamId],
    queryFn: async () => {
      const res = await craApi.getDashboard(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const { data: crossModuleData } = useQuery({
    queryKey: ['cra-cross-module', teamId],
    queryFn: async () => {
      const res = await craApi.getCrossModule(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const { data: examReadiness } = useQuery({
    queryKey: ['cra-exam-readiness', teamId],
    queryFn: async () => {
      const res = await craApi.getExamReadiness(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const seedTasksMutation = useMutation({
    mutationFn: () => craApi.seedCraTasks(teamId!),
    onSuccess: () => toast.success('CRA recurring tasks created in Compliance Calendar'),
    onError: () => toast.error('Failed to create CRA tasks'),
  });

  // CRAD reports (the Small/ISB CRA evaluation with the LTD / AA / GD / BPD conclusions).
  const { data: cradList } = useQuery({
    queryKey: ['crad-list', teamId],
    queryFn: async () => (await craApi.listCrads(teamId!)).data,
    enabled: !!teamId,
  });
  const crads: any[] = cradList?.reports ?? [];

  // Institution name + FDIC cert + regulator for pre-filling a new CRAD report. Sourced from the
  // institution-setup endpoint (not the peer config) because it falls back to the Team name and
  // carries the prudential regulator — so a bank that's signed in always sees its own details
  // pre-filled rather than a blank form.
  const { data: instSetup } = useQuery({
    queryKey: ['institutionSetup', teamId],
    queryFn: async () => (await teamApi.getInstitutionSetup(teamId!)).data,
    enabled: !!teamId,
  });

  const createCradMutation = useMutation({
    mutationFn: (body: any) => craApi.createCrad(teamId!, body),
    onSuccess: (r) => {
      setShowCradModal(false);
      toast.success('CRAD report created — click “Compute CRAD” to run the analysis.');
      navigate(`/cra/crad/${r.data.id}`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to create CRAD report'),
  });

  // Export the CRA Appendix (Appendix B) — Excel or editable Word.
  const exportAppendixMutation = useMutation({
    mutationFn: async (format: 'xlsx' | 'docx') => {
      const res = await craApi.exportAppendix(teamId!, format);
      const type = format === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type });
      const cd = (res.headers?.['content-disposition'] as string | undefined) || '';
      const name = cd.match(/filename="?([^"]+)"?/)?.[1] || `CRA_Appendix.${format}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onError: () => toast.error('Failed to export the CRA Appendix.'),
  });
  const exportingFormat = exportAppendixMutation.isPending ? exportAppendixMutation.variables : null;

  if (teamLoading || isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-gray-200 rounded-lg" />)}
        </div>
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  if (featuresLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  if (!hasFeature('module_cra')) {
    return (
      <div className="text-center py-16">
        <ChartBarSquareIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-xl font-semibold text-gray-900">CRA Dashboard</h2>
        <p className="mt-2 text-gray-600 max-w-md mx-auto">
          CRA Compliance is available on the Enterprise plan. Upgrade to access assessment area analysis, lending tests, and AI-powered gap analysis.
        </p>
      </div>
    );
  }

  const dashboard = data?.dashboard;
  const healthScore = dashboard?.healthScore ?? 0;
  const bankCategory = dashboard?.config?.bankCategory ?? 'INTERMEDIATE';

  const frameworkLabel = bankCategory === 'SMALL'
    ? { title: 'Streamlined CRA Evaluation', desc: 'Lending Test only' }
    : bankCategory === 'LARGE'
      ? { title: 'Full CRA Evaluation', desc: 'Lending, Investment/CD, and Service Tests' }
      : { title: 'Standard CRA Evaluation', desc: 'Lending Test + Community Development Test' };

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-navy-900">CRA Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">Community Reinvestment Act compliance management and analysis</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-md border border-ocean-300 bg-white overflow-hidden">
            <button
              onClick={() => exportAppendixMutation.mutate('xlsx')}
              disabled={exportAppendixMutation.isPending}
              title="Download the CRA Appendix (Appendix B) as Excel"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-ocean-700 hover:bg-ocean-50 disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {exportingFormat === 'xlsx' ? 'Exporting…' : 'Appendix · Excel'}
            </button>
            <span className="h-5 w-px bg-ocean-200" />
            <button
              onClick={() => exportAppendixMutation.mutate('docx')}
              disabled={exportAppendixMutation.isPending}
              title="Download the CRA Appendix (Appendix B) as an editable Word document"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-ocean-700 hover:bg-ocean-50 disabled:opacity-50"
            >
              {exportingFormat === 'docx' ? 'Exporting…' : 'Word'}
            </button>
          </div>
          <button
            onClick={() => setConfigModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50"
          >
            <Cog6ToothIcon className="h-4 w-4" />
            Configure
          </button>
        </div>
      </div>

      {/* Framework Banner */}
      <div className="mb-6 flex items-center gap-2 rounded-lg bg-ocean-50 border border-ocean-100 px-4 py-2.5">
        <span className="text-sm font-medium text-ocean-700">{frameworkLabel.title}</span>
        <span className="text-sm text-ocean-600">— {frameworkLabel.desc}</span>
      </div>

      {/* Health Score + Summary Cards — or a get-started strip before any AA exists,
          so a brand-new account sees guidance instead of a row of zeroes/N-A. */}
      {(dashboard?.assessmentAreaCount ?? 0) === 0 ? (
        <div className="mb-8 rounded-lg border border-ocean-100 bg-ocean-50 px-6 py-8 text-center">
          <MapPinIcon className="mx-auto h-10 w-10 text-ocean-400" />
          <h3 className="mt-3 text-base font-semibold text-navy-900">Get started with CRA</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-600">
            Add your first assessment area to unlock your CRA health score, lending test, and community development tracking.
          </p>
          <Link
            to="/cra/assessment-areas"
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700"
          >
            <MapPinIcon className="h-4 w-4" /> Set up an assessment area
          </Link>
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${healthScore >= 70 ? 'bg-green-50' : healthScore >= 40 ? 'bg-amber-50' : 'bg-red-50'}`}>
              <ChartBarSquareIcon className={`h-5 w-5 ${healthScore >= 70 ? 'text-green-600' : healthScore >= 40 ? 'text-amber-600' : 'text-red-600'}`} />
            </div>
            <div>
              <p className="text-sm text-gray-500">CRA Health Score</p>
              <p className="text-2xl font-semibold text-navy-900 tabular-nums">{healthScore}/100</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ocean-50">
              <MapPinIcon className="h-5 w-5 text-ocean-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Assessment Areas</p>
              <p className="text-2xl font-semibold text-navy-900 tabular-nums">{dashboard?.assessmentAreaCount ?? 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ocean-50">
              <ScaleIcon className="h-5 w-5 text-ocean-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">LMI Lending</p>
              <p className="text-2xl font-semibold text-navy-900 tabular-nums">
                {dashboard?.lendingAnalysis?.lmiOriginationPct != null
                  ? `${dashboard.lendingAnalysis.lmiOriginationPct.toFixed(1)}%`
                  : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-ocean-50">
              <BuildingOfficeIcon className="h-5 w-5 text-ocean-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">
                CD Activities{bankCategory === 'SMALL' && <span className="ml-1 text-xs text-gray-400">(Optional)</span>}
              </p>
              <p className="text-2xl font-semibold text-navy-900 tabular-nums">{dashboard?.cdSummary?.totalActivities ?? 0}</p>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Charts */}
      {(dashboard?.lendingAnalysis || dashboard?.cdSummary?.byType) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {dashboard?.lendingAnalysis && (
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Lending by Tract Income Level</h3>
              <LendingDistributionChart analysis={dashboard.lendingAnalysis} />
            </div>
          )}
          {dashboard?.cdSummary?.byType && Object.keys(dashboard.cdSummary.byType).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">CD Activities by Type</h3>
              {bankCategory === 'SMALL' && <p className="text-xs text-gray-400 mb-3">Not required for your CRA exam, but tracked for reference</p>}
              {bankCategory !== 'SMALL' && <div className="mb-4" />}
              <CdActivityChart byType={dashboard.cdSummary.byType} />
            </div>
          )}
        </div>
      )}

      {/* Exam Readiness */}
      {examReadiness && (
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6 mb-8">
          <div className="flex items-start gap-6">
            {/* Score Circle */}
            <div className="shrink-0 flex flex-col items-center">
              <div className={`relative flex h-24 w-24 items-center justify-center rounded-full border-4 ${
                examReadiness.score >= 75 ? 'border-green-500' : examReadiness.score >= 50 ? 'border-amber-500' : 'border-red-500'
              }`}>
                <span className={`text-3xl font-bold tabular-nums ${
                  examReadiness.score >= 75 ? 'text-green-600' : examReadiness.score >= 50 ? 'text-amber-600' : 'text-red-600'
                }`}>{examReadiness.score}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Exam Ready</p>
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">CRA Exam Readiness</h3>

              {/* Checklist */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-4">
                {examReadiness.items?.map((item: any, i: number) => (
                  <div key={i} className="flex items-start gap-2">
                    {item.passed ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    ) : (
                      <XCircleIcon className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className={`text-sm ${item.passed ? 'text-gray-700' : 'text-gray-900 font-medium'}`}>{item.label}</p>
                      <p className="text-xs text-gray-400 truncate">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* AI Narrative */}
              {examReadiness.aiNarrative && (
                <div className="rounded-md bg-ocean-50 border border-ocean-100 px-4 py-3">
                  <p className="text-sm text-ocean-700">{examReadiness.aiNarrative}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CRAD Report — the full Small/ISB CRA evaluation (LTD, AA Concentration, Geographic,
          Borrower's Profile conclusions + memorandum). This is the "CRAD computed" exam-readiness item. */}
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-ocean-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">CRAD Report</h2>
              <p className="text-sm text-gray-500">Performance-criteria conclusions (Loan-to-Deposit, AA Concentration, Geographic, Borrower's Profile) + exam memorandum.</p>
            </div>
          </div>
          <button
            // A Large bank is evaluated under three tests and has its own CRAD; sending it to the
            // small-bank report produced the wrong evaluation (2026-10-15).
            onClick={() => (bankCategory === 'LARGE' ? navigate('/cra/large-bank') : setShowCradModal(true))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-ocean-600 px-3 py-2 text-sm font-medium text-white hover:bg-ocean-700"
          >
            {bankCategory === 'LARGE' ? 'Open Large Bank evaluations' : '+ Generate CRAD report'}
          </button>
        </div>
        <div className="p-6">
          {crads.length === 0 ? (
            <p className="text-sm text-gray-500">No CRAD report yet. Generate one to compute the formal CRA performance conclusions (incl. the Loan-to-Deposit ratio) and a draft exam memorandum.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {crads.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-2.5">
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">{c.bankName || 'CRAD report'}</span>
                    <span className="text-gray-500"> · {c.bankCategory} · {c.evaluationPeriod || '—'}</span>
                    {c.overallRating && <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{c.overallRating}</span>}
                    <span className="ml-2 text-xs text-gray-400">{c.status}</span>
                  </div>
                  <button onClick={() => navigate(`/cra/crad/${c.id}`)}
                    className="rounded-md border border-ocean-300 px-3 py-1.5 text-sm font-medium text-ocean-700 hover:bg-ocean-50">
                    Open
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Generate CRAD modal */}
      {showCradModal && (
        <CradCreateModal
          defaultBankName={instSetup?.institutionName || ''}
          defaultCert={instSetup?.fdicCertNumber ?? null}
          defaultCategory={dashboard?.config?.bankCategory || 'INTERMEDIATE'}
          // institution-setup stores the Fed as 'FED'; the modal dropdown uses 'FRB'.
          defaultRegulator={
            (instSetup?.primaryRegulator === 'FED' ? 'FRB' : instSetup?.primaryRegulator)
            || dashboard?.config?.primaryRegulator || 'FDIC'
          }
          submitting={createCradMutation.isPending}
          onCancel={() => setShowCradModal(false)}
          onSubmit={(body) => {
            // "Large" in this dialog used to create a small-bank CRAD. Large banks have their own
            // evaluation (Lending, Investment and Service Tests).
            if (body?.bankCategory === 'LARGE') {
              setShowCradModal(false);
              toast('Large banks use the Large Bank evaluation (Lending, Investment and Service Tests).');
              navigate('/cra/large-bank');
              return;
            }
            createCradMutation.mutate(body);
          }}
        />
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
          </div>
          <div className="p-6 grid grid-cols-2 gap-3">
            <Link
              to="/cra/assessment-areas"
              className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors text-center"
            >
              <MapPinIcon className="h-6 w-6 mx-auto text-ocean-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">Assessment Areas</p>
            </Link>
            <Link
              to="/cra/lending-analysis"
              className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors text-center"
            >
              <ScaleIcon className="h-6 w-6 mx-auto text-ocean-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">Lending Analysis</p>
            </Link>
            <Link
              to="/cra/cd-activities"
              className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors text-center"
            >
              <BuildingOfficeIcon className="h-6 w-6 mx-auto text-ocean-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">CD Activities</p>
              {bankCategory === 'SMALL' && <p className="text-xs text-gray-400 mt-0.5">Optional for your exam</p>}
            </Link>
            <Link
              to="/cra/gap-analysis"
              className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors text-center"
            >
              <ExclamationTriangleIcon className="h-6 w-6 mx-auto text-ocean-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">Gap Analysis</p>
            </Link>
            <button
              onClick={() => seedTasksMutation.mutate()}
              disabled={seedTasksMutation.isPending}
              className="rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors text-center col-span-2 disabled:opacity-50"
            >
              <CalendarDaysIcon className="h-6 w-6 mx-auto text-ocean-600 mb-2" />
              <p className="text-sm font-medium text-gray-900">
                {seedTasksMutation.isPending ? 'Creating...' : 'Seed CRA Calendar Tasks'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Annual AA review, quarterly CD reporting, public file update</p>
            </button>
          </div>
        </div>

        {/* Cross-Module Risk Indicators */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Cross-Module Risk Indicators</h2>
          </div>
          <div className="p-6">
            {crossModuleData ? (
              <div className="space-y-3">
                {crossModuleData.fairLending && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Fair Lending Risk</span>
                    <span className={`font-medium ${crossModuleData.fairLending.overallRiskLevel === 'HIGH' ? 'text-red-600' : crossModuleData.fairLending.overallRiskLevel === 'MEDIUM' ? 'text-amber-600' : 'text-green-600'}`}>
                      {crossModuleData.fairLending.overallRiskLevel || 'N/A'}
                    </span>
                  </div>
                )}
                {crossModuleData.regression?.length > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Regression Findings</span>
                    <span className="font-medium text-navy-900">{crossModuleData.regression.length} analyses</span>
                  </div>
                )}
                {crossModuleData.enforcement?.length > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">CRA Enforcement Actions</span>
                    <span className="font-medium text-red-600">{crossModuleData.enforcement.length} relevant</span>
                  </div>
                )}
                {!crossModuleData.fairLending && !crossModuleData.regression?.length && !crossModuleData.enforcement?.length && (
                  <p className="text-sm text-gray-500">No cross-module data available yet. Run analyses in Fair Lending and HMDA modules to see correlations.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">Loading cross-module data...</p>
            )}
          </div>
        </div>
      </div>

      {/* Assessment Area Summary */}
      {dashboard?.assessmentAreas?.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Assessment Area Summary</h2>
            <Link to="/cra/assessment-areas" className="text-sm text-ocean-600 hover:text-ocean-700">View All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Tracts</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LMI Tracts</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">LMI Population</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Majority-Minority</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dashboard.assessmentAreas.map((aa: any) => (
                  <tr key={aa.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{aa.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 tabular-nums">{aa.totalTracts}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 tabular-nums">{aa.lmiTracts}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 tabular-nums">{aa.lmiPopulationPct?.toFixed(1) ?? 0}%</td>
                    <td className="px-6 py-4 text-sm text-gray-600 tabular-nums">{aa.majorityMinority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {configModalOpen && (
        <CraConfigModal
          teamId={teamId!}
          onClose={() => setConfigModalOpen(false)}
        />
      )}
    </div>
  );
}

const INCOME_COLORS = { Low: '#dc2626', Moderate: '#d97706', Middle: '#2563eb', Upper: '#16a34a' };

function LendingDistributionChart({ analysis }: { analysis: any }) {
  const data = [
    { name: 'Low', value: analysis.lowIncomeTracts || 0 },
    { name: 'Moderate', value: analysis.moderateIncomeTracts || 0 },
    { name: 'Middle', value: analysis.middleIncomeTracts || 0 },
    { name: 'Upper', value: analysis.upperIncomeTracts || 0 },
  ].filter(d => d.value > 0);

  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <p className="text-sm text-gray-500">No lending data available</p>;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percent }: any) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
          {data.map(d => <Cell key={d.name} fill={INCOME_COLORS[d.name as keyof typeof INCOME_COLORS]} />)}
        </Pie>
        <Tooltip formatter={(value: any) => [value, 'Originations']} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function CdActivityChart({ byType }: { byType: Record<string, { count: number; amount: number }> }) {
  const data = Object.entries(byType).map(([type, val]) => ({
    name: type.charAt(0) + type.slice(1).toLowerCase(),
    count: val.count,
    amount: Math.round(val.amount / 1000),
  }));

  if (data.length === 0) return <p className="text-sm text-gray-500">No CD activity data</p>;

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
        <Tooltip formatter={(value: any, name: any) => [name === 'amount' ? `$${value}K` : value, name === 'amount' ? 'Amount ($K)' : 'Count']} />
        <Legend />
        <Bar yAxisId="left" dataKey="count" fill="#0891b2" name="Count" radius={[4, 4, 0, 0]} />
        <Bar yAxisId="right" dataKey="amount" fill="#06b6d4" name="Amount ($K)" radius={[4, 4, 0, 0]} opacity={0.6} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CraConfigModal({ teamId, onClose }: { teamId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    bankCategory: 'INTERMEDIATE',
    totalAssets: '',
    currentRating: 'SATISFACTORY',
    autoAnalysis: true,
    includeInHealthScore: true,
  });

  const { data: configData } = useQuery({
    queryKey: ['cra-config', teamId],
    queryFn: async () => {
      const res = await craApi.getConfig(teamId);
      return res.data;
    },
    enabled: !!teamId,
  });

  // Populate form when config loads. GET /cra/config returns the configuration row itself (not { config }):
  // reading .config left the form on its defaults, so saving it reset a Large Bank to Intermediate.
  useEffect(() => {
    if (configData) {
      setFormData({
        bankCategory: configData.bankCategory || 'INTERMEDIATE',
        totalAssets: configData.totalAssets?.toString() || '',
        currentRating: configData.currentRating || 'SATISFACTORY',
        autoAnalysis: configData.autoAnalysis ?? true,
        includeInHealthScore: configData.includeInHealthScore ?? true,
      });
    }
  }, [configData]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => craApi.updateConfig(teamId, data),
    onSuccess: () => {
      toast.success('CRA configuration updated');
      queryClient.invalidateQueries({ queryKey: ['cra-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['cra-config'] });
      onClose();
    },
    onError: () => toast.error('Failed to update configuration'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      ...formData,
      totalAssets: formData.totalAssets ? parseFloat(formData.totalAssets) : null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">CRA Configuration</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Assets ($M)</label>
            <input
              type="number"
              value={formData.totalAssets}
              onChange={e => {
                const val = e.target.value;
                const assets = parseFloat(val);
                const suggested = !isNaN(assets)
                  ? assets < 600 ? 'SMALL' : assets >= 2000 ? 'LARGE' : 'INTERMEDIATE'
                  : undefined;
                setFormData(prev => ({
                  ...prev,
                  totalAssets: val,
                  ...(suggested ? { bankCategory: suggested } : {}),
                }));
              }}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              placeholder="e.g., 500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Bank Category</label>
            <select
              value={formData.bankCategory}
              onChange={e => setFormData(prev => ({ ...prev, bankCategory: e.target.value }))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            >
              <option value="SMALL">Small (assets &lt; $600M) — Lending Test</option>
              <option value="INTERMEDIATE">Intermediate ($600M–$2B) — Lending + CD Test</option>
              <option value="LARGE">Large (assets &gt; $2B) — Lending, Investment/CD, Service Tests</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">Auto-suggested from asset size. Override if needed.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current CRA Rating</label>
            <select
              value={formData.currentRating}
              onChange={e => setFormData(prev => ({ ...prev, currentRating: e.target.value }))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            >
              <option value="OUTSTANDING">Outstanding</option>
              <option value="SATISFACTORY">Satisfactory</option>
              <option value="NEEDS_TO_IMPROVE">Needs to Improve</option>
              <option value="SUBSTANTIAL_NONCOMPLIANCE">Substantial Noncompliance</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoAnalysis"
              checked={formData.autoAnalysis}
              onChange={e => setFormData(prev => ({ ...prev, autoAnalysis: e.target.checked }))}
              className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
            />
            <label htmlFor="autoAnalysis" className="text-sm text-gray-700">Auto-run lending analysis on HMDA upload</label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="includeInHealthScore"
              checked={formData.includeInHealthScore}
              onChange={e => setFormData(prev => ({ ...prev, includeInHealthScore: e.target.checked }))}
              className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
            />
            <label htmlFor="includeInHealthScore" className="text-sm text-gray-700">Include CRA in dashboard health score</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Generate CRAD report modal ───────────────────────────────────────────── */
function CradCreateModal({
  defaultBankName, defaultCert, defaultCategory, defaultRegulator, submitting, onCancel, onSubmit,
}: {
  defaultBankName: string;
  defaultCert: number | null;
  defaultCategory: string;
  defaultRegulator: string;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (body: any) => void;
}) {
  const thisYear = new Date().getFullYear();
  const [bankName, setBankName] = useState(defaultBankName);
  const [fdicCert, setFdicCert] = useState(defaultCert != null ? String(defaultCert) : '');
  const [category, setCategory] = useState(defaultCategory);
  const [regulator, setRegulator] = useState(defaultRegulator);
  const [yearsStr, setYearsStr] = useState(`${thisYear - 1}, ${thisYear - 2}`);

  const submit = () => {
    if (!bankName.trim()) { toast.error('Bank name is required'); return; }
    const years = yearsStr.split(',').map((s) => Number(s.trim())).filter((y) => Number.isFinite(y) && y >= 2018);
    const evaluationPeriod = years.length ? `${Math.min(...years)}–${Math.max(...years)}` : '';
    onSubmit({
      bankName: bankName.trim(),
      fdicCertNumber: fdicCert ? Number(fdicCert) : null,
      bankCategory: category,
      primaryRegulator: regulator,
      evaluationPeriod,
      evaluationYears: years,
      productTypes: ['HMDA', 'SMALL_BUSINESS'],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-navy-900">Generate CRAD Report</h3>
        <p className="mt-1 text-sm text-gray-600">
          Creates the formal CRA evaluation (Loan-to-Deposit, AA Concentration, Geographic &amp; Borrower's Profile
          conclusions). You can compute it and refine on the next screen.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600">Bank name</label>
            <input value={bankName} onChange={(e) => setBankName(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="The Bank of …" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600">FDIC cert (optional)</label>
              <input type="number" value={fdicCert} onChange={(e) => setFdicCert(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Bank category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="SMALL">Small</option>
                <option value="INTERMEDIATE">Intermediate Small Bank</option>
                <option value="LARGE">Large</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600">Primary regulator</label>
              <select value={regulator} onChange={(e) => setRegulator(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="FDIC">FDIC</option>
                <option value="FRB">FRB</option>
                <option value="OCC">OCC</option>
                <option value="NCUA">NCUA</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Evaluation years</label>
              <input value={yearsStr} onChange={(e) => setYearsStr(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" placeholder="2024, 2023" />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={submitting}
            className="rounded-lg bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create & open'}
          </button>
        </div>
      </div>
    </div>
  );
}
